<template>
  <div class="">
    <div class="flex">
      <div class="flex-none" :style="{'width': firstEndcap }" />
      <div class="flex-auto border-l border-black" :class="{'border-r': outlet == 1 || section == 2}">
        <div class="flex">
          <div class="arrow-left">&nbsp;</div>
          <div class="flex-auto mt-2 border-t border-black">&nbsp;</div>
          <div class="px-2 text-center">{{ feet }} feet / {{ total }} meters</div>
          <div class="flex-auto mt-2 border-t border-black">&nbsp;</div>
          <div class="arrow-right">&nbsp;</div>
        </div>
        <!-- length -->
        <div class="flex items-center justify-center my-6">
          <div class="flex px-4">
            <b class="mx-2">Flow</b>
            <div v-if="! toRightFlow" class="arrow-left">&nbsp;</div>
            <div class="w-8 h-px mt-2 bg-black">&nbsp;</div>
            <div v-if="toRightFlow" class="arrow-right">&nbsp;</div>
          </div>
        </div>
        <!-- Flow -->
      </div>
      <div class="flex-none" :style="{'width': lastEndcap }" />
    </div>

    <div class="flex text-xs text-center text-blue-800">
      <div v-for="(p, i) in partsWithoutBO" :key="p.id" class="flex flex-col grow shrink" :style="getChannelWidth(p)">
        <end-cap
          v-if="isEndCap(p)"
          :part="p"
          :selected="part.part_id == p.part_id"
          :to-right-flow="toRightFlow"
          :height="getNextPartHeight(i)"
          @input="setChannel(p)"
        />
        <span v-else>
          <p class="overflow-x-hidden uppercase" style="font-size: 10px;">{{ p.position_label }}</p>
          <p style="font-size: 10px;">{{ p.length }}m </p>
          <part
            :id="`r${run}s${section}p${i}`"
            :ref="`part_${i}`"
            :index="getIndex(p)"
            :part="p"
            :selected="part"
            :angle="angle"
            :plain-view="plainView"
            :to-right-flow="toRightFlow"
            :is-last-channel="isLastPart(i)"
            :bottom-outlet="bottomOutlet"
            @click="setChannel(p)"
          />
          <div class="flex">
            <depth-label
              :p="p"
              :bottom-outlet="bottomOutlet"
              :is-first-channel="isFirstChannel(p)"
              :is-last-channel="isLastChannel(p)"
              :class="{'order-1' :! toRightFlow }"
              class="flex-auto"
            />
            <bottom-outlet
              v-if="bottomOutlet.index == i || p.outlet_size"
              :p="p"
              :plain-view="plainView"
              :to-right-flow="toRightFlow"
              :bottom-outlet="bottomOutlet"
              :class="{'order-0' : ! toRightFlow }"
              @input:model="setChannel($event)"
            />
          </div>
        </span>
      </div>
      <!-- parts -->
      <div v-if="channels.length === 0">
        <p class="px-6 py-4 border-t" colspan="4">No runs found.</p>
      </div>
    </div>
    <!-- parts -->
  </div>
</template>

<script>

import Part from './Part.vue'
import EndCap from './EndCap.vue'
import BottomOutlet from './BO.vue'
import DepthLabel from './Depth.vue'

export default {
  components: {
    Part,
    EndCap,
    BottomOutlet,
    DepthLabel,
  },
  props: {
    isMounted  : Boolean,
    plainView  : Boolean,
    part       : Object,
    outlets    : Object,
    parts      : Array,
    depths     : Array,
    section    : String,
    run        : Number,
    outlet     : Number,
    firstEndcap: String,
    lastEndcap : String,
    toRightFlow: Boolean,
  },
  emits: ['input'],
  data () {
    return {
      angle: '0',
    }
  },
  computed:{
    total() {
      return this.parts.map((ob) => parseFloat(ob.length)).reduce((partial, a) => partial + a , 0)
    },
    feet () {
      return this.MetricToFeet(this.total)
    },
    channels () {
      return this.parts.filter(el => el.part_type == 'channel' || (el.part_type == 'accessory' && el.type == 'cb'))
    },
    partsWithoutBO () {
      return this.parts.filter(el => ! this.isBottomOutlet(el))
    },
    bottomOutlet () {
      if (! this.isMounted) {
        return false
      }

      let item = this.parts.find(el => this.isBottomOutlet(el))
      if (item) {
        let index = this.parts.indexOf(item)
        if (this.toRightFlow) {
          item.index = index - 1
        } else {
          // Skip non-channel accessories (e.g. PMCC connector) before the first real channel
          let firstChannelIdx = this.partsWithoutBO.findIndex(p => p.part_type === 'channel')
          item.index = firstChannelIdx >= 0 ? firstChannelIdx : index
        }
        return item
      }

      return false
    },
    sectionOneIsDoubleEndOutlet() {
      return this.outlet == this.outlets.DoubleEnd && this.section == 1
    },
    sectionTwoIsCentralOutlet () {
      return this.outlet == this.outlets.Central && this.section == 2
    },
  },
  watch: {
    isMounted() {
      setTimeout(() => {
        this.searchDepthAngle()
      }, 50)
    },
  },
  methods: {
    getPart (part, index) {
      if (! this.isMounted) {
        return false
      }

      if (part && this.isEndCap(part)) {
        let ref = this.$refs[`part_${index}`]
        if (ref) {
          let height = ref[0].channelHeight
          part.height = height
          return part
        }
      }

      return false
    },
    getChannelWidth (part) {
      if(this.isEndCap(part)) {
        if (part.outlet_size) {
          return { 'width': '40px'}
        }
        return { 'width': '25px'}
      }

      if (part.type == 'mtm') {
        return { 'width': '10px'}
      }

      let { length } = part
      if (this.channels.length < 2) {
        length = 1
      }

      return {'flex-basis': `${length * 100}%`}
    },
    setChannel (part) {
      this.$emit('input', part.id)
    },
    isFirstChannel(p) {
      let parts = this.parts.filter(el => el.part_type == 'channel')
      if (parts.length > 0) {
        return p.id == parts[0].id
      }
      return false
    },
    isLastChannel (p) {
      let parts = this.parts.filter(el => el.part_type == 'channel')
      if (parts.length > 0) {
        return p.id == parts[parts.length - 1].id
      }
      return false
    },
    isLastPart(index) {
      switch (this.outlet) {
      case this.outlets.Central:
        if (this.section == 2) {
          return index == 0
        }
        return index == this.partsWithoutBO.length - 1
      case this.outlets.DoubleEnd:
        if (this.section == 2) {
          return index == this.partsWithoutBO.length - 2
        }
        return index == 1
      default:
        return index == this.partsWithoutBO.length - 2
      }
    },
    getDepthChannelLabel (index) {
      let part = this.channels[index + 1]
      if (! part) {
        part = this.channels[index - 1]
      }

      return part.depth_label
    },
    getIndex(part) {
      if (part.part_type == 'accessory' && part.type == 'cb'){
        return this.depths.length
      }

      let i = this.depths.indexOf(part.depth)
      return i
    },
    reverseIndex(index) {
      if(index === -1) {
        return index
      }

      return this.depths.length - index - 1
    },
    isEndCap(part) {
      let accesories = [ 'ec', 'ce']
      return part.part_type == 'accessory' && accesories.includes(part.type)
    },
    isBottomOutlet(part) {
      return part.part_type == 'accessory' && part.type == 'bo'
    },
    getNextPartHeight(index) {
      if (index == 0) {
        return this.$refs['part_1']?.[0]?.channelHeight
      }
      return this.$refs[`part_${index - 1}`]?.[0]?.channelHeight
    },
    searchDepthAngle () {
      let depths = this.parts.filter(el => el.part_type == 'channel' && el.neutral == false).sort((a, b) => a.position - b.position)
      if (depths.length > 0) {
        let a = depths[0]
        let b = depths[depths.length - 1]

        a = this.getRecElementFromPart(a.part_id)
        b = this.getRecElementFromPart(b.part_id)

        if (this.sectionOneIsDoubleEndOutlet || this.sectionTwoIsCentralOutlet ) {
          this.angle = this.calculateAngle(b, a).toString()
        } else {
          this.angle = this.calculateAngle(a, b).toString()
        }
      }
    },
    getRecElementFromPart (part_id) {
      let index = this.partsWithoutBO.findIndex(el => el.part_id == part_id && el.part_type == 'channel')
      let elem = document.getElementById('r'+ this.run + 's' + this.section + 'p' + index)
      let rect = elem.getBoundingClientRect()

      return  { x: rect.x + rect.width, y: rect.y + rect.height }
    },
    calculateAngle (a, b) {
      let distanceX = b.x - a.x
      let distanceY = b.y - a.y

      let angle = Math.atan2(distanceY, distanceX)
      angle *= 180 / Math.PI

      return angle.toFixed(1)
    },
  },
}
</script>

<style>

.arrow-left {
    width: 5px;
    height: 5px;
    margin-top: 3.5px;
    border-top: 5px solid transparent;
    border-right: 5px solid black;
    border-bottom: 5px solid transparent;
}

.arrow-right {
    width: 5px;
    height: 5px;
    margin-top: 3.5px;
    border-top: 5px solid transparent;
    border-left: 5px solid black;
    border-bottom: 5px solid transparent;
}
</style>
